import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { format, startOfWeek, endOfWeek, parseISO } from 'date-fns'
import { apiClient, getErrorMessage } from '../../api/client'
import { useOfflineFirstQuery } from '../../hooks/useOfflineFirstQuery'
import { useAuthStore } from '../../stores/authStore'
import { resolveTenantSlug } from '../../lib/tenant'

type CalendarSession = {
  id: number
  title: string
  start: string
  end: string
  color: string
  client_name: string
  status: string
  service_code: string
}

function toDateInputValue(d: Date): string {
  return format(d, 'yyyy-MM-dd')
}

export default function SchedulePage() {
  const tenantSlug = resolveTenantSlug()
  const user = useAuthStore((s) => s.user)

  const initialStart = useMemo(() => startOfWeek(new Date(), { weekStartsOn: 1 }), [])
  const initialEnd = useMemo(() => endOfWeek(new Date(), { weekStartsOn: 1 }), [])

  const [start, setStart] = useState<string>(toDateInputValue(initialStart))
  const [end, setEnd] = useState<string>(toDateInputValue(initialEnd))

  const cacheKey = useMemo(() => {
    return `tenant:${tenantSlug ?? 'unknown'}:schedule:calendar:start=${start}:end=${end}`
  }, [end, start, tenantSlug])

  const query = useOfflineFirstQuery<CalendarSession[]>({
    queryKey: ['sessions', 'calendar', { start, end }],
    cacheKey,
    cacheMaxAgeMs: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await apiClient.get<CalendarSession[]>('/api/v1/sessions/calendar/', {
        params: { start, end },
      })
      return res.data
    },
  })

  const canCreateSession = user?.role === 'admin' || user?.role === 'bcba' || user?.role === 'rbt'
  const sessions = query.data ?? []

  const grouped = useMemo(() => {
    const map = new Map<string, CalendarSession[]>()
    for (const session of sessions) {
      const dateKey = session.start.slice(0, 10)
      const existing = map.get(dateKey) ?? []
      existing.push(session)
      map.set(dateKey, existing)
    }
    for (const [k, v] of map.entries()) {
      v.sort((a, b) => a.start.localeCompare(b.start))
      map.set(k, v)
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b))
  }, [query.data])

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Schedule</h1>
          <p className="text-sm text-gray-600">
            Tenant-scoped calendar view. No cross-tenant data is visible.
          </p>
        </div>
        {canCreateSession && (
          <Link to="/app/sessions/new" className="btn-primary">
            New session
          </Link>
        )}
      </div>

      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="label" htmlFor="start">
              Start
            </label>
            <input id="start" type="date" className="input mt-1" value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div>
            <label className="label" htmlFor="end">
              End
            </label>
            <input id="end" type="date" className="input mt-1" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              className="btn-secondary w-full"
              onClick={() => {
                setStart(toDateInputValue(initialStart))
                setEnd(toDateInputValue(initialEnd))
              }}
            >
              Current week
            </button>
          </div>
        </div>
      </div>

      {query.isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {getErrorMessage(query.error)}
        </div>
      )}

      {query.isLoading && sessions.length === 0 ? (
        <div className="space-y-4">
          {Array.from({ length: 4 }).map((_, idx) => (
            <div key={idx} className="card p-6">
              <div className="h-5 w-40 bg-gray-200 rounded animate-pulse" />
              <div className="mt-4 space-y-2">
                <div className="h-10 bg-gray-200 rounded animate-pulse" />
                <div className="h-10 bg-gray-200 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      ) : grouped.length === 0 ? (
        <div className="card p-6 text-sm text-gray-600">No sessions found for this range.</div>
      ) : (
        <div className="space-y-4">
          {grouped.map(([dateKey, sessions]) => (
            <div key={dateKey} className="card overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="font-semibold text-gray-900">
                  {format(parseISO(dateKey), 'EEEE, MMM d, yyyy')}
                </h2>
              </div>
              <div className="divide-y divide-gray-200">
                {sessions.map((s) => (
                  <Link
                    key={s.id}
                    to={`/app/sessions/${s.id}`}
                    className="px-6 py-4 flex items-center justify-between hover:bg-gray-50"
                  >
                    <div>
                      <div className="font-medium text-gray-900">{s.client_name}</div>
                      <div className="text-sm text-gray-600">
                        {format(parseISO(s.start), 'p')} - {format(parseISO(s.end), 'p')} | {s.service_code}
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">{s.status.replace('_', ' ')}</div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
